'use client';

import {
	AlertTriangle,
	BadgeCheck,
	Building2,
	CalendarCheck,
	CheckCircle2,
	Clock3,
	Plus,
	RefreshCw,
	ShieldCheck,
	WalletCards
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	CompleteFieldShiftRequest,
	CreateFieldShiftRequest,
	CreateFieldSiteRequest,
	FieldOperationsCapabilityItem,
	FieldOperationsChecklistItem,
	FieldOperationsChecklistStatus,
	FieldOperationsRiskItem,
	FieldOperationsRiskSeverity,
	FieldOperationsSummaryResponse,
	FieldShiftResponse,
	FieldShiftStatus,
	FieldSiteOperation,
	FieldWageType
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type FieldSiteFormState = {
	name: string;
	locationName: string;
	managerEmployeeId: string;
	allowedRadiusMeters: string;
};

type FieldShiftFormState = {
	siteId: string;
	employeeId: string;
	employeeName: string;
	scheduledStartAt: string;
	scheduledEndAt: string;
	wageType: FieldWageType;
	wageAmount: string;
};

type FieldOperationsPageMode = 'field' | 'shift';

const pageCopy: Record<FieldOperationsPageMode, { title: string; description: string }> = {
	field: {
		title: '현장 운영',
		description: '매장 스케줄, 위치 출퇴근, 인건비 자동계산과 근로기준 리스크를 관리합니다.'
	},
	shift: {
		title: '교대근무',
		description: '교대근무 스케줄, 공유 상태, 출퇴근 완료와 인건비 리스크를 관리합니다.'
	}
};

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function wonLabel(value: number | null | undefined): string {
	return `${numberLabel(value)}원`;
}

function minutesLabel(minutes: number | null | undefined): string {
	if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return '0분';
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	if (hours <= 0) return `${rest}분`;
	return rest > 0 ? `${hours}시간 ${rest}분` : `${hours}시간`;
}

function dateLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', { month: '2-digit', day: '2-digit' }).format(date);
}

function timeLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' }).format(date);
}

function checklistStatusLabel(status: FieldOperationsChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: FieldOperationsChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(
	severity: FieldOperationsRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function shiftStatusLabel(status: FieldShiftStatus): string {
	const labels: Record<FieldShiftStatus, string> = {
		DRAFT: '초안',
		PUBLISHED: '공유',
		CHECKED_IN: '근무중',
		COMPLETED: '완료',
		APPROVED: '승인',
		CANCELLED: '취소'
	};
	return labels[status] ?? status;
}

function shiftStatusVariant(status: FieldShiftStatus): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'COMPLETED' || status === 'APPROVED') return 'success';
	if (status === 'DRAFT' || status === 'CHECKED_IN') return 'warning';
	if (status === 'CANCELLED') return 'destructive';
	return 'outline';
}

function wageTypeLabel(type: FieldWageType): string {
	if (type === 'HOURLY') return '시급';
	if (type === 'DAILY') return '일급';
	return '월급';
}

function wageAmount(shift: FieldShiftResponse): number | null {
	if (shift.wageType === 'HOURLY') return shift.hourlyWage;
	if (shift.wageType === 'DAILY') return shift.dailyWage;
	return shift.monthlySalary;
}

function shiftTimeLabel(shift: FieldShiftResponse): string {
	return `${dateLabel(shift.scheduledStartAt)} ${timeLabel(shift.scheduledStartAt)}-${timeLabel(
		shift.scheduledEndAt
	)}`;
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: FieldOperationsChecklistItem[] }) {
	return (
		<div className="grid gap-3 md:grid-cols-2">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: FieldOperationsRiskItem[] }) {
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">운영 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function SiteOperationsTable({ sites }: { sites: FieldSiteOperation[] }) {
	if (sites.length === 0) return <EmptyState message="등록된 현장 운영 데이터가 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[640px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">매장</th>
						<th className="px-4 py-3 font-medium">스케줄</th>
						<th className="px-4 py-3 font-medium">출퇴근</th>
						<th className="px-4 py-3 font-medium">인건비</th>
						<th className="px-4 py-3 font-medium">상태</th>
					</tr>
				</thead>
				<tbody>
					{sites.map((site) => (
						<tr key={site.siteId} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<p className="font-semibold">{site.siteName}</p>
								<p className="mt-1 text-xs text-muted-foreground">{site.locationName ?? '위치 미등록'}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								예정 {numberLabel(site.scheduledShiftCount)}개 · 완료 {numberLabel(site.completedShiftCount)}개
							</td>
							<td className="px-4 py-4 text-muted-foreground">기록 {numberLabel(site.checkedInCount)}건</td>
							<td className="px-4 py-4 text-muted-foreground">{wonLabel(site.laborCost)}</td>
							<td className="px-4 py-4">
								<Badge variant={site.staffingStatus === '정상' ? 'success' : 'warning'}>
									{site.staffingStatus}
								</Badge>
								<p className="mt-2 text-xs text-muted-foreground">{site.riskLabel}</p>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function ShiftList({
	shifts,
	onPublish,
	onComplete,
	loadingId
}: {
	shifts: FieldShiftResponse[];
	onPublish: (shift: FieldShiftResponse) => void;
	onComplete: (shift: FieldShiftResponse) => void;
	loadingId: number | null;
}) {
	if (shifts.length === 0) return <EmptyState message="예정된 교대근무가 없습니다." />;
	return (
		<div className="space-y-3">
			{shifts.map((shift) => (
				<div key={shift.id} className="rounded-lg border border-border bg-background p-4">
					<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2">
								<p className="font-semibold">{shift.employeeName}</p>
								<Badge variant={shiftStatusVariant(shift.status)}>{shiftStatusLabel(shift.status)}</Badge>
								<Badge variant={shift.sharedToMobile ? 'success' : 'outline'}>
									{shift.sharedToMobile ? '모바일 공유' : '미공유'}
								</Badge>
							</div>
							<p className="mt-1 text-sm text-muted-foreground">
								{shift.siteName} · {shiftTimeLabel(shift)}
							</p>
							<div className="mt-3 flex flex-wrap gap-2">
								<Badge variant="outline">
									{wageTypeLabel(shift.wageType)} {wonLabel(wageAmount(shift))}
								</Badge>
								<Badge variant="outline">예정 {minutesLabel(shift.scheduledWorkMinutes)}</Badge>
								<Badge variant={shift.locationVerified ? 'success' : 'warning'}>
									{shift.locationVerified ? 'GPS 인증' : 'GPS 확인'}
								</Badge>
								{shift.lateMinutes > 0 ? <Badge variant="warning">지각 {minutesLabel(shift.lateMinutes)}</Badge> : null}
							</div>
							{shift.complianceNote ? (
								<p className="mt-3 text-sm text-muted-foreground">{shift.complianceNote}</p>
							) : null}
						</div>
						<div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
							{shift.status === 'DRAFT' ? (
								<Button
									type="button"
									size="sm"
									variant="outline"
									disabled={loadingId === shift.id}
									onClick={() => onPublish(shift)}
								>
									<BadgeCheck className="size-4" />
									공유
								</Button>
							) : null}
							{shift.status === 'PUBLISHED' || shift.status === 'CHECKED_IN' ? (
								<Button
									type="button"
									size="sm"
									disabled={loadingId === shift.id}
									onClick={() => onComplete(shift)}
								>
									<CheckCircle2 className="size-4" />
									완료
								</Button>
							) : null}
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

function Capabilities({ capabilities }: { capabilities: FieldOperationsCapabilityItem[] }) {
	return (
		<div className="grid gap-3 sm:grid-cols-2">
			{capabilities.map((capability) => (
				<div key={capability.code} className="rounded-lg border border-border bg-background p-4">
					<p className="font-semibold">{capability.label}</p>
					<p className="mt-2 text-sm text-muted-foreground">{capability.description}</p>
				</div>
			))}
		</div>
	);
}

export function FieldOperationsPage({ mode = 'field' }: { mode?: FieldOperationsPageMode } = {}) {
	const [state, setState] = useState<RequestState<FieldOperationsSummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [operationLoading, setOperationLoading] = useState<string | null>(null);
	const [shiftActionId, setShiftActionId] = useState<number | null>(null);
	const [siteForm, setSiteForm] = useState<FieldSiteFormState>({
		name: '',
		locationName: '',
		managerEmployeeId: '',
		allowedRadiusMeters: '100'
	});
	const [shiftForm, setShiftForm] = useState<FieldShiftFormState>({
		siteId: '',
		employeeId: '',
		employeeName: '',
		scheduledStartAt: '',
		scheduledEndAt: '',
		wageType: 'HOURLY',
		wageAmount: ''
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<FieldOperationsSummaryResponse>('/field-operations/summary');
				setState({ status: 'success', data: summary });
				setShiftForm((current) =>
					current.siteId || summary.siteOperations.length === 0
						? current
						: { ...current, siteId: String(summary.siteOperations[0].siteId) }
				);
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submitSite(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('site');
		setActionMessage(null);
		try {
			const request: CreateFieldSiteRequest = {
				name: siteForm.name.trim(),
				locationName: siteForm.locationName.trim() || undefined,
				managerEmployeeId: siteForm.managerEmployeeId ? Number(siteForm.managerEmployeeId) : null,
				allowedRadiusMeters: siteForm.allowedRadiusMeters ? Number(siteForm.allowedRadiusMeters) : 100
			};
			await api.post('/field-operations/sites', request);
			setSiteForm({ name: '', locationName: '', managerEmployeeId: '', allowedRadiusMeters: '100' });
			setActionMessage('매장을 추가했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function submitShift(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!shiftForm.siteId) {
			setActionMessage('매장을 먼저 선택하세요.');
			return;
		}
		setOperationLoading('shift');
		setActionMessage(null);
		try {
			const wageAmountValue = shiftForm.wageAmount ? Number(shiftForm.wageAmount) : null;
			const request: CreateFieldShiftRequest = {
				siteId: Number(shiftForm.siteId),
				employeeId: Number(shiftForm.employeeId),
				employeeName: shiftForm.employeeName.trim(),
				scheduledStartAt: shiftForm.scheduledStartAt,
				scheduledEndAt: shiftForm.scheduledEndAt,
				wageType: shiftForm.wageType,
				hourlyWage: shiftForm.wageType === 'HOURLY' ? wageAmountValue : null,
				dailyWage: shiftForm.wageType === 'DAILY' ? wageAmountValue : null,
				monthlySalary: shiftForm.wageType === 'MONTHLY' ? wageAmountValue : null
			};
			await api.post('/field-operations/shifts', request);
			setShiftForm((current) => ({
				...current,
				employeeId: '',
				employeeName: '',
				scheduledStartAt: '',
				scheduledEndAt: '',
				wageAmount: ''
			}));
			setActionMessage('교대근무 초안을 만들었습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function publishShift(shift: FieldShiftResponse) {
		setShiftActionId(shift.id);
		setActionMessage(null);
		try {
			await api.patch(`/field-operations/shifts/${shift.id}/publish`);
			setActionMessage('스케줄을 공유했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setShiftActionId(null);
		}
	}

	async function completeShift(shift: FieldShiftResponse) {
		setShiftActionId(shift.id);
		setActionMessage(null);
		try {
			const request: CompleteFieldShiftRequest = {
				actualCheckInAt: shift.actualCheckInAt ?? shift.scheduledStartAt,
				actualCheckOutAt: shift.actualCheckOutAt ?? shift.scheduledEndAt,
				locationVerified: true,
				managerApproved: true,
				memo: '운영 화면 승인'
			};
			await api.patch(`/field-operations/shifts/${shift.id}/complete`, request);
			setActionMessage('근무 기록을 완료 처리했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setShiftActionId(null);
		}
	}

	const data = state.status === 'success' ? state.data : null;
	const copy = pageCopy[mode];

	return (
		<div className="space-y-6">
			<PageHeader
				title={copy.title}
				description={copy.description}
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<StatCard
							title="활성 매장"
							value={data.activeSiteCount}
							description={`전체 ${numberLabel(data.totalSiteCount)}개`}
							icon={Building2}
						/>
						<StatCard
							title="교대근무"
							value={data.scheduledShiftCount}
							description={`공유 ${numberLabel(data.publishedShiftCount)} · 완료 ${numberLabel(data.completedShiftCount)}`}
							icon={CalendarCheck}
						/>
						<StatCard
							title="실제 인건비"
							value={wonLabel(data.actualLaborCost)}
							description={`예정 ${wonLabel(data.estimatedLaborCost)}`}
							icon={WalletCards}
						/>
						<StatCard
							title="준법 리스크"
							value={data.complianceRiskCount}
							description={`최저시급 ${wonLabel(data.minimumHourlyWage)}`}
							icon={ShieldCheck}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>현장 운영 입력</CardTitle>
							<CardDescription>매장과 교대근무 초안을 생성하고 즉시 운영 현황에 반영합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="grid gap-5 xl:grid-cols-2">
								<form className="space-y-3 rounded-lg border border-border bg-background p-4" onSubmit={submitSite}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="field-site-name">매장명</Label>
											<Input
												id="field-site-name"
												value={siteForm.name}
												onChange={(event) => setSiteForm((current) => ({ ...current, name: event.target.value }))}
												required
											/>
										</div>
										<div>
											<Label htmlFor="field-site-location">위치</Label>
											<Input
												id="field-site-location"
												value={siteForm.locationName}
												onChange={(event) =>
													setSiteForm((current) => ({ ...current, locationName: event.target.value }))
												}
											/>
										</div>
										<div>
											<Label htmlFor="field-site-manager">매니저 직원 ID</Label>
											<Input
												id="field-site-manager"
												type="number"
												value={siteForm.managerEmployeeId}
												onChange={(event) =>
													setSiteForm((current) => ({ ...current, managerEmployeeId: event.target.value }))
												}
											/>
										</div>
										<div>
											<Label htmlFor="field-site-radius">GPS 반경 m</Label>
											<Input
												id="field-site-radius"
												type="number"
												min="10"
												value={siteForm.allowedRadiusMeters}
												onChange={(event) =>
													setSiteForm((current) => ({ ...current, allowedRadiusMeters: event.target.value }))
												}
											/>
										</div>
									</div>
									<Button type="submit" size="sm" disabled={operationLoading === 'site'}>
										<Plus className="size-4" />
										매장 추가
									</Button>
								</form>

								<form className="space-y-3 rounded-lg border border-border bg-background p-4" onSubmit={submitShift}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="field-shift-site">매장</Label>
											<select
												id="field-shift-site"
												className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
												value={shiftForm.siteId}
												onChange={(event) =>
													setShiftForm((current) => ({ ...current, siteId: event.target.value }))
												}
												required
											>
												<option value="">선택</option>
												{data.siteOperations.map((site) => (
													<option key={site.siteId} value={site.siteId}>
														{site.siteName}
													</option>
												))}
											</select>
										</div>
										<div>
											<Label htmlFor="field-shift-employee-id">직원 ID</Label>
											<Input
												id="field-shift-employee-id"
												type="number"
												value={shiftForm.employeeId}
												onChange={(event) =>
													setShiftForm((current) => ({ ...current, employeeId: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="field-shift-employee-name">직원명</Label>
											<Input
												id="field-shift-employee-name"
												value={shiftForm.employeeName}
												onChange={(event) =>
													setShiftForm((current) => ({ ...current, employeeName: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="field-shift-wage-type">임금 유형</Label>
											<select
												id="field-shift-wage-type"
												className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
												value={shiftForm.wageType}
												onChange={(event) =>
													setShiftForm((current) => ({
														...current,
														wageType: event.target.value as FieldWageType
													}))
												}
											>
												<option value="HOURLY">시급</option>
												<option value="DAILY">일급</option>
												<option value="MONTHLY">월급</option>
											</select>
										</div>
										<div>
											<Label htmlFor="field-shift-start">시작</Label>
											<Input
												id="field-shift-start"
												type="datetime-local"
												value={shiftForm.scheduledStartAt}
												onChange={(event) =>
													setShiftForm((current) => ({ ...current, scheduledStartAt: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="field-shift-end">종료</Label>
											<Input
												id="field-shift-end"
												type="datetime-local"
												value={shiftForm.scheduledEndAt}
												onChange={(event) =>
													setShiftForm((current) => ({ ...current, scheduledEndAt: event.target.value }))
												}
												required
											/>
										</div>
										<div className="sm:col-span-2">
											<Label htmlFor="field-shift-wage">임금액</Label>
											<Input
												id="field-shift-wage"
												type="number"
												min="1"
												value={shiftForm.wageAmount}
												onChange={(event) =>
													setShiftForm((current) => ({ ...current, wageAmount: event.target.value }))
												}
												required
											/>
										</div>
									</div>
									<Button type="submit" size="sm" disabled={operationLoading === 'shift'}>
										<Plus className="size-4" />
										스케줄 초안
									</Button>
								</form>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>운영 체크</CardTitle>
							<CardDescription>
								스케줄 공유, GPS 출퇴근, 실시간 인건비, 급여명세서 준비, 근로기준 리스크를 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							<ChecklistGrid items={data.checklist} />
							<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
								<RiskPanel risks={data.riskItems} />
								<div className="min-w-0 rounded-lg border border-border bg-background p-4">
									<div className="mb-4 flex items-center gap-2">
										<Building2 className="size-4 text-muted-foreground" />
										<p className="font-semibold">매장별 상태</p>
									</div>
									<div className="overflow-x-auto">
										<SiteOperationsTable sites={data.siteOperations} />
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
						<Card>
							<CardHeader>
								<CardTitle>교대근무</CardTitle>
								<CardDescription>공유, 위치 인증, 지각, 완료 처리 상태를 한 번에 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ShiftList
									shifts={data.upcomingShifts}
									onPublish={(shift) => void publishShift(shift)}
									onComplete={(shift) => void completeShift(shift)}
									loadingId={shiftActionId}
								/>
							</CardContent>
						</Card>

						<div className="space-y-4">
							<div className="rounded-lg border border-border bg-muted/20 p-4">
								<div className="flex items-center gap-2">
									<Clock3 className="size-4 text-muted-foreground" />
									<p className="font-semibold">근태 품질</p>
								</div>
								<div className="mt-4 grid grid-cols-2 gap-3 text-sm">
									<div>
										<p className="text-muted-foreground">GPS 인증</p>
										<p className="mt-1 font-semibold">
											{numberLabel(data.locationVerifiedCount)} / {numberLabel(data.completedShiftCount)}
										</p>
									</div>
									<div>
										<p className="text-muted-foreground">지각</p>
										<p className="mt-1 font-semibold">{numberLabel(data.lateShiftCount)}건</p>
									</div>
									<div>
										<p className="text-muted-foreground">인증률</p>
										<p className="mt-1 font-semibold">{numberLabel(data.averageCoveragePercent)}%</p>
									</div>
									<div>
										<p className="text-muted-foreground">최저시급</p>
										<p className="mt-1 font-semibold">{wonLabel(data.minimumHourlyWage)}</p>
									</div>
								</div>
							</div>
							<Capabilities capabilities={data.capabilities} />
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}
